import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Gift, Sparkles } from "lucide-react";
import { BRAND } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GIFT_TEST_IDS = {
  packageCard: "gift-package-card",
  buyerName: "gift-buyer-name",
  buyerEmail: "gift-buyer-email",
  recipientName: "gift-recipient-name",
  recipientEmail: "gift-recipient-email",
  message: "gift-message",
  submit: "gift-checkout-submit",
};

const Cadeaubonnen = () => {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    recipient_name: "",
    recipient_email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/gift-vouchers/packages`)
      .then((r) => {
        setPackages(r.data);
        if (r.data.length) setSelected(r.data[1]?.id || r.data[0].id);
      })
      .catch(() => toast.error("Kon pakketten niet laden."));
  }, []);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Kies eerst een pakket.");
      return;
    }
    const cleaned = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== "" && v != null)
    );
    const payload = {
      package_id: selected,
      origin_url: window.location.origin,
      ...cleaned,
    };
    if (selected === "voucher_custom") {
      const amt = parseFloat(customAmount);
      if (!amt || amt < 25 || amt > 2000) {
        toast.error("Vul een bedrag in tussen €25 en €2000.");
        return;
      }
      payload.custom_amount = amt;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/gift-vouchers/checkout`, payload);
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Er ging iets mis. Probeer het opnieuw.";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-16 bg-white overflow-hidden">
        <div className="container-x text-center max-w-3xl mx-auto">
          <p className="eyebrow mb-4">Cadeaubonnen</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Geef een <span className="italic text-gold">herinnering</span>
            <br />
            in plaats van een ding.
          </h1>
          <p className="mt-6 text-neutral-600 leading-relaxed max-w-xl mx-auto">
            De mooiste cadeaus zijn onvergetelijk. Geef een fotoshoot cadeau aan wie je liefhebt —
            elegant geleverd met een persoonlijke boodschap.
          </p>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-beige py-20 md:py-24">
        <div className="container-x">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6">
            {packages.map((p, i) => {
              const active = selected === p.id;
              return (
                <motion.button
                  key={p.id}
                  data-testid={`${GIFT_TEST_IDS.packageCard}-${p.id}`}
                  type="button"
                  onClick={() => setSelected(p.id)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06 }}
                  className={`text-left p-6 md:p-8 transition-all ${
                    active
                      ? "bg-ink text-white ring-2 ring-gold"
                      : "bg-white text-ink hover:ring-1 hover:ring-gold"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Gift size={18} className="text-gold" />
                    {active && (
                      <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Gekozen</span>
                    )}
                  </div>
                  <p className={`font-serif text-4xl mt-6 ${active ? "text-gold" : "text-ink"}`}>
                    €{p.amount.toFixed(0)}
                  </p>
                  <p className="font-serif text-xl mt-2">{p.label}</p>
                  <p className={`text-xs mt-3 leading-relaxed ${active ? "text-white/60" : "text-neutral-500"}`}>
                    {p.description}
                  </p>
                </motion.button>
              );
            })}

            {/* Custom amount card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: packages.length * 0.06 }}
              className={`p-6 md:p-8 transition-all cursor-pointer ${
                selected === "voucher_custom"
                  ? "bg-ink text-white ring-2 ring-gold"
                  : "bg-white text-ink hover:ring-1 hover:ring-gold border border-dashed border-gold/40"
              }`}
              onClick={() => setSelected("voucher_custom")}
              data-testid={`${GIFT_TEST_IDS.packageCard}-voucher_custom`}
            >
              <div className="flex items-center justify-between">
                <Sparkles size={18} className="text-gold" />
                {selected === "voucher_custom" && (
                  <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Gekozen</span>
                )}
              </div>
              <p className="font-script text-gold text-3xl mt-6 leading-none">
                Kies zelf
              </p>
              <p className="font-serif text-xl mt-2">Vrij bedrag</p>
              {selected === "voucher_custom" ? (
                <div className="mt-4">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
                    Bedrag (€25 – €2000)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-2xl text-gold">€</span>
                    <input
                      data-testid="gift-custom-amount"
                      type="number"
                      min={25}
                      max={2000}
                      step={5}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="75"
                      className="w-full bg-transparent border-b border-white/30 focus:border-gold outline-none py-1 text-white text-xl font-serif"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-xs mt-3 leading-relaxed text-neutral-500">
                  Bepaal zelf hoeveel je wilt geven — tussen €25 en €2000.
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-narrow max-w-3xl">
          <div className="text-center mb-12">
            <p className="eyebrow mb-3">Personaliseer</p>
            <h2 className="font-serif text-4xl md:text-5xl">Aan wie geef je dit cadeau?</h2>
          </div>
          <form onSubmit={submit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <span className="eyebrow block mb-2 text-neutral-500">Jouw naam</span>
                <input
                  data-testid={GIFT_TEST_IDS.buyerName}
                  value={form.buyer_name}
                  onChange={update("buyer_name")}
                  placeholder="Jane Doe"
                  className="w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent"
                />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2 text-neutral-500">Jouw e-mail</span>
                <input
                  data-testid={GIFT_TEST_IDS.buyerEmail}
                  type="email"
                  value={form.buyer_email}
                  onChange={update("buyer_email")}
                  placeholder="jane@voorbeeld.be"
                  className="w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent"
                />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2 text-neutral-500">Voor wie is het</span>
                <input
                  data-testid={GIFT_TEST_IDS.recipientName}
                  value={form.recipient_name}
                  onChange={update("recipient_name")}
                  placeholder="Sam & Alex"
                  className="w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent"
                />
              </label>
              <label className="block">
                <span className="eyebrow block mb-2 text-neutral-500">E-mail ontvanger (optioneel)</span>
                <input
                  data-testid={GIFT_TEST_IDS.recipientEmail}
                  type="email"
                  value={form.recipient_email}
                  onChange={update("recipient_email")}
                  placeholder="sam@voorbeeld.be"
                  className="w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent"
                />
              </label>
            </div>
            <label className="block">
              <span className="eyebrow block mb-2 text-neutral-500">Persoonlijke boodschap</span>
              <textarea
                data-testid={GIFT_TEST_IDS.message}
                value={form.message}
                onChange={update("message")}
                rows={4}
                maxLength={400}
                placeholder="Een klein woord voor een groot moment..."
                className="w-full border-b border-neutral-300 focus:border-gold outline-none py-2 bg-transparent resize-none"
              />
            </label>

            <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t border-neutral-200">
              <div className="flex items-center gap-3 text-sm text-neutral-600">
                <Check size={16} className="text-gold" />
                <span>Direct geleverd via e-mail — met unieke voucher-code.</span>
              </div>
              <button
                data-testid={GIFT_TEST_IDS.submit}
                type="submit"
                disabled={loading || !selected}
                className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Bezig..." : "Bestel cadeaubon"}
              </button>
            </div>
          </form>

          <p className="text-xs text-neutral-500 text-center mt-8">
            Beveiligde betaling via Stripe. Voor vragen: {BRAND.email}
          </p>
        </div>
      </section>
    </>
  );
};

export default Cadeaubonnen;
